/* A job on the board.
 *
 * Reading order is deliberate: colour chip and sheen first, then the code, then
 * who it is for. Dates sit in tinted boxes so "when did it come in / when must
 * it go out" is answerable without reading a label. Everything below the rule
 * is spec — profile, colour code, item count, finish. */
import type { CSSProperties, DragEvent } from 'react';
import type { Job, Plan } from '@/types';
import { SHEENS, coatsFor } from '@/data/reference';
import { areaFor, profileOf } from '@/domain/items';
import { atRisk, isOverdue } from '@/domain/schedule';
import { formatDay } from '@/domain/time';
import { colourFor, useApp } from '@/store/useApp';
import { Avatar, SheenBars, Swatch } from '@/ui/Swatch';
import { usePageGutter, withGutter } from '@/ui/viewport';

const factValue = (size: number, urgent = false): CSSProperties => ({
  fontFamily: 'var(--font-data)',
  fontSize: size,
  fontWeight: 500,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: urgent ? 'var(--urgent)' : undefined,
});

const nudgeStyle = (live: boolean, size: number): CSSProperties => ({
  width: size,
  height: size,
  borderRadius: 8,
  border: `1px solid ${live ? 'var(--line)' : 'transparent'}`,
  background: live ? 'var(--on-accent)' : 'transparent',
  color: live ? 'var(--ink-muted)' : 'var(--chip-border)',
  fontSize: 13,
  lineHeight: 1,
  cursor: live ? 'pointer' : 'default',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/** How much room the card has. All four draw the same card — the same facts in
 *  the same order — and differ only in scale: `full` is the canvas card at its
 *  designed 392px, `full-tight` is that same card at a width a tablet can show
 *  three of, `fit` is the card squeezed into a column that shares the screen
 *  with every other stage, and `snug` is `fit` again for a screen that cannot
 *  give every stage a fit-width column — an iPad, mostly.
 *
 *  `full-tight` keeps the full card's *layout* — the chip beside the code and
 *  the customer, the two dates side by side, the four spec facts two-up — and
 *  gives up only type and padding. That is the whole point of it: the shop
 *  working a stage on an iPad reads the card it knows, and sees another column
 *  of the board beside it.
 *
 *  Nothing is dropped on the way down. A narrow column is answered by making
 *  the type smaller and letting the two grids fall to a single column, not by
 *  hiding the spec — someone reading the board still wants the profile, the
 *  colour name, the m² and the coats without opening the job. What `snug` buys
 *  is height: a shorter card is more cards down a column, which is the thing an
 *  iPad is actually short of. */
export type CardSize = 'full' | 'full-tight' | 'fit' | 'snug';

/** The four scales side by side, so the step between them is one thing to read
 *  rather than twenty scattered ternaries. `factMin` and `dateMin` are what make
 *  a narrow column work: the grids are `auto-fit`, so they sit two-up while
 *  there is room for two and stack when there isn't. Two is the ceiling as well
 *  as the aim — the spec is read in pairs at every scale (see `pairs`).
 *
 *  Type and spacing step down; `nudge`, `foot` and `avatar` barely move. The
 *  narrow scales are what a touch screen gets, and shrinking the one thing on
 *  the card that is pressed with a glove on would be the wrong economy. */
const SCALE = {
  full: {
    pad: 15,
    radius: 14,
    gap: 11,
    swatch: 56,
    terseSwatch: 30,
    terseDot: 6,
    headGap: 13,
    sheenGap: 5,
    sheenAlign: 'flex-start',
    stackHead: false,
    headGapY: 3,
    headTextGap: 3,
    code: 19,
    customer: 17,
    title: 13.5,
    value: 15,
    sub: 12,
    label: 10,
    avatar: 26,
    nudge: 30,
    foot: 32,
    boxPad: '7px 10px',
    boxRadius: 10,
    rowGap: 9,
    colGap: 14,
    dateMin: 150,
    factMin: 150,
    rule: 10,
    lines: 1,
  },
  /* The full card for a tablet. Every measurement here is the full card's a
     step down — nothing changes shape. The two `min`s are what hold that: at a
     296px column the card has about 256px of content, so the dates still sit
     two-up and the spec still falls into two columns of two, exactly as they do
     at 392. `lines: 1` with them, because a title that wrapped would make the
     card taller than the one it is a smaller copy of. */
  'full-tight': {
    pad: 11,
    radius: 13,
    gap: 8,
    swatch: 44,
    terseSwatch: 26,
    terseDot: 6,
    headGap: 10,
    sheenGap: 4,
    sheenAlign: 'flex-start',
    stackHead: false,
    headGapY: 3,
    headTextGap: 2,
    code: 16,
    customer: 14.5,
    title: 12,
    value: 13.5,
    sub: 11,
    label: 9.5,
    avatar: 24,
    nudge: 28,
    foot: 30,
    boxPad: '5px 8px',
    boxRadius: 9,
    rowGap: 7,
    colGap: 11,
    dateMin: 112,
    factMin: 104,
    rule: 8,
    lines: 1,
  },
  fit: {
    pad: 10,
    radius: 11,
    gap: 7,
    swatch: 34,
    terseSwatch: 22,
    terseDot: 5,
    headGap: 8,
    sheenGap: 4,
    sheenAlign: 'center',
    stackHead: true,
    headGapY: 5,
    headTextGap: 2,
    code: 14,
    customer: 12.5,
    title: 11,
    value: 13,
    sub: 10.5,
    label: 9,
    avatar: 22,
    nudge: 26,
    foot: 28,
    boxPad: '4px 7px',
    boxRadius: 8,
    rowGap: 6,
    colGap: 10,
    dateMin: 92,
    factMin: 70,
    rule: 7,
    lines: 2,
  },
  snug: {
    pad: 8,
    radius: 10,
    gap: 5,
    swatch: 28,
    terseSwatch: 20,
    terseDot: 5,
    headGap: 7,
    sheenGap: 3,
    sheenAlign: 'center',
    stackHead: true,
    headGapY: 4,
    headTextGap: 1,
    code: 12.5,
    customer: 11.5,
    title: 10,
    value: 11.5,
    sub: 9.5,
    label: 8.5,
    avatar: 21,
    nudge: 26,
    foot: 28,
    boxPad: '3px 6px',
    boxRadius: 7,
    rowGap: 4,
    colGap: 8,
    dateMin: 80,
    factMin: 60,
    rule: 6,
    lines: 2,
  },
} as const;

type Scale = (typeof SCALE)[CardSize];

interface JobCardProps {
  job: Job;
  plan: Plan | undefined;
  /** Position within its stage, for the up/down nudge affordances. */
  index: number;
  count: number;
  size?: CardSize;
  /** Draw the short card: the chip small enough that the code sits beside it,
   *  no date in, no labels over the values, and overdue said with the card's
   *  own ground rather than a badge. It is what a column gets on a screen that
   *  can only give it a hundred-odd pixels wide — the same facts in fewer
   *  lines, and opening the column trades width back for the rest of them. */
  terse?: boolean;
}

export function JobCard({ job, plan, index, count, size = 'full', terse = false }: JobCardProps) {
  const dropAt = useApp((s) => s.dropAt);
  const stages = useApp((s) => s.stages);
  const openJob = useApp((s) => s.openJob);
  const beginDrag = useApp((s) => s.beginDrag);
  const endDrag = useApp((s) => s.endDrag);
  const hoverCard = useApp((s) => s.hoverCard);
  const dropOnCard = useApp((s) => s.dropOnCard);
  const nudge = useApp((s) => s.nudge);

  const colour = useApp((s) => colourFor(s, job.colour));
  const overdue = isOverdue(job, stages);
  // Planned finish past the deadline, or already late: either way the date out
  // is the thing to look at.
  const late = atRisk(plan) || overdue;
  const profile = profileOf(job);
  // What the deadline is qualified by: the promised date when the two differ,
  // and on the short card the word the badge would have said. A status is never
  // colour alone, whatever the card is short of.
  const promisedSub =
    plan && job.promised !== null && plan.deadline !== job.promised
      ? `promised ${formatDay(job.promised)}`
      : '';
  const outSub =
    terse && overdue ? (promisedSub ? `Overdue · ${promisedSub}` : 'Overdue') : promisedSub;

  const hint = dropAt?.code === job.code;
  const z: Scale = SCALE[size];
  const grid = (min: number): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    rowGap: z.rowGap,
    columnGap: z.colGap,
  });

  /* The spec is read two-up wherever it can be read two-up at all — profile
     beside colour, items beside finish — because that is the card the shop
     already knows off the full board, and a fit column wide enough for three
     across would put finish on a line of its own under items. Half the column
     is the floor on a track, so a third can never fit; `factMin` is still what
     drops the pair to one column when even half is too little. */
  const pairs: CSSProperties = {
    ...grid(z.factMin),
    gridTemplateColumns: `repeat(auto-fit, minmax(max(${z.factMin}px, calc(50% - ${z.colGap / 2}px)), 1fr))`,
  };

  /* Short, the chip is the width of a small sheen row rather than of a 41px
     one — which is the whole reason the job code fits on the line beside it. */
  const chip = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: terse ? 2 : z.sheenGap,
        alignItems: terse ? 'center' : z.sheenAlign,
        flex: 'none',
      }}
    >
      <Swatch code={job.colour} size={terse ? z.terseSwatch : z.swatch} />
      <SheenBars
        level={job.sheen}
        dot={terse ? z.terseDot : 8}
        label={`Sheen — ${SHEENS[job.sheen]}`}
      />
    </div>
  );

  /* The badge sits beside the code where there is room and drops under it where
     there isn't. The short card is the one place it goes: there the whole card
     turns urgent, and the word it said is carried under the date out. */
  const codeRow = (
    <div
      style={{
        minWidth: 0,
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        flexWrap: terse ? 'nowrap' : 'wrap',
        // Short, the row is one line by construction, so it also has to be the
        // one that gives — a code that overran would push the card wider than
        // its column rather than wrap under the chip.
        overflow: terse ? 'hidden' : undefined,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: z.code,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {job.code}
      </span>
      {overdue && !terse ? <OverdueBadge small={size !== 'full'} /> : null}
    </div>
  );

  const who = (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: z.headTextGap }}>
      <div
        style={{
          fontSize: z.customer,
          fontWeight: 700,
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {job.customer}
      </div>
      <div
        style={{
          fontSize: z.title,
          color: 'var(--ink-muted)',
          lineHeight: 1.25,
          overflow: 'hidden',
          ...clamp(z.lines),
        }}
      >
        {job.title}
      </div>
    </div>
  );

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    hoverCard(job.code, e.clientY < r.top + r.height / 2);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dropOnCard(job.code);
      }}
      style={{
        borderTop: `3px solid ${hint && dropAt.before ? 'var(--accent)' : 'transparent'}`,
        borderBottom: `3px solid ${hint && !dropAt.before ? 'var(--accent)' : 'transparent'}`,
        borderRadius: 3,
      }}
    >
      <div
        className="blueprint"
        draggable
        role="button"
        tabIndex={0}
        onDragStart={(e) => {
          beginDrag(job.code);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={endDrag}
        onClick={() => openJob(job.code)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openJob(job.code);
          }
        }}
        style={{
          // Short, an overdue job is the colour of the card. It is the one
          // thing the shop wanted to read without stopping on it, and a badge
          // costs a line on a card that is short of them.
          background: terse && overdue ? 'var(--urgent-tint)' : 'var(--surface)',
          padding: z.pad,
          borderRadius: z.radius,
          cursor: 'pointer',
          // Overdue is a red left edge *and* a labelled badge — never colour alone.
          borderLeft: overdue ? '3px solid var(--urgent)' : '1px solid var(--line)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: z.gap }}>
          {/* Who it is for is the line most likely to be a long name, so at the
              narrow scales it drops below the chip and takes the card's whole
              width rather than sharing it with a 41px sheen indicator. */}
          {z.stackHead ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: z.headGapY }}>
              <div style={{ display: 'flex', gap: terse ? 6 : z.headGap, alignItems: 'center' }}>
                {chip}
                {codeRow}
              </div>
              {who}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: z.headGap, alignItems: 'flex-start' }}>
              {chip}
              <div
                style={{
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: z.headTextGap,
                }}
              >
                {codeRow}
                {who}
              </div>
            </div>
          )}

          {/* Short, only the date still to happen is drawn — when it came in is
              a fact about last week, and the card is short of lines. */}
          <div style={terse ? undefined : grid(z.dateMin)}>
            {terse ? null : (
              <DateBox
                z={z}
                label="Date in"
                value={formatDay(job.received)}
                background="var(--date-in-bg)"
                line="var(--date-in-line)"
              />
            )}
            <DateBox
              z={z}
              bare={terse}
              label="Date out"
              value={formatDay(plan?.deadline)}
              sub={outSub}
              subUrgent={terse && overdue}
              urgent={late}
              background={
                terse && overdue
                  ? 'color-mix(in srgb, var(--urgent) 13%, var(--surface))'
                  : late
                    ? 'var(--urgent-tint)'
                    : 'var(--date-out-bg)'
              }
              line={late ? 'var(--urgent)' : 'var(--date-out-line)'}
            />
          </div>

          <div
            style={{
              ...pairs,
              paddingTop: z.rule,
              borderTop: '1px solid var(--line)',
            }}
          >
            <Fact z={z} bare={terse} label="Profile" value={profile.main} sub={profile.mixed} />
            <Fact z={z} bare={terse} label="Colour" value={colour.code} sub={colour.name} />
            <Fact
              z={z}
              bare={terse}
              label="Items"
              value={`${job.items}`}
              sub={`${areaFor(job).toFixed(1)} m²`}
            />
            <Fact
              z={z}
              bare={terse}
              label="Finish"
              value={SHEENS[job.sheen]}
              sub={`2-pac PU · ${coatsFor(job.sheen)} coats`}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 4,
              rowGap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
              minHeight: z.foot,
            }}
          >
            <button
              type="button"
              title="Move up in this stage"
              aria-label={`Move ${job.code} up in this stage`}
              onClick={(e) => {
                e.stopPropagation();
                nudge(job.code, -1);
              }}
              style={nudgeStyle(index > 0, z.nudge)}
            >
              ↑
            </button>
            <button
              type="button"
              title="Move down in this stage"
              aria-label={`Move ${job.code} down in this stage`}
              onClick={(e) => {
                e.stopPropagation();
                nudge(job.code, 1);
              }}
              style={nudgeStyle(index < count - 1, z.nudge)}
            >
              ↓
            </button>
            <span style={{ marginLeft: 'auto' }} />
            {job.who.map((w) => (
              <Avatar key={w} initials={w} size={z.avatar} />
            ))}
            {job.who.length === 0 ? (
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-muted)',
                }}
              >
                Nobody on it
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** One line, or two lines and then an ellipsis — the narrow scales give a long
 *  job title a second line rather than cutting it in half. */
function clamp(lines: number): CSSProperties {
  if (lines <= 1) return { whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
  } as CSSProperties;
}

export function OverdueBadge({ small }: { small?: boolean }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-heading)',
        fontSize: small ? 9 : 10,
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--on-urgent)',
        background: 'var(--urgent)',
        padding: small ? '2px 6px' : '3px 8px',
        borderRadius: 999,
      }}
    >
      Overdue
    </span>
  );
}

function DateBox({
  z,
  bare,
  label,
  value,
  sub,
  subUrgent,
  urgent,
  background,
  line,
}: {
  z: Scale;
  /** Drop the label and draw the value alone. The tinted box, the mono figure
   *  and its being the only date on the card are enough to say what it is. */
  bare?: boolean;
  label: string;
  value: string;
  sub?: string;
  subUrgent?: boolean;
  urgent?: boolean;
  background: string;
  line: string;
}) {
  return (
    <div
      style={{
        minWidth: 0,
        padding: z.boxPad,
        borderRadius: z.boxRadius,
        background,
        border: `1px solid ${line}`,
      }}
    >
      {bare ? null : (
        <div className="label-xs" style={{ fontSize: z.label }}>
          {label}
        </div>
      )}
      <div style={factValue(z.value, urgent)} title={bare ? label : undefined} aria-label={label}>
        {value}
      </div>
      {sub ? (
        <Sub z={z} urgent={subUrgent}>
          {sub}
        </Sub>
      ) : null}
    </div>
  );
}

function Fact({
  z,
  bare,
  label,
  value,
  sub,
}: {
  z: Scale;
  bare?: boolean;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      {bare ? null : (
        <div className="label-xs" style={{ fontSize: z.label }}>
          {label}
        </div>
      )}
      {/* Bare, the label is still there for anyone who asks the value what it
          is — a pointer on it, and the same word to a screen reader. */}
      <div style={factValue(z.value)} title={bare ? label : undefined} aria-label={label}>
        {value}
      </div>
      {sub ? <Sub z={z}>{sub}</Sub> : null}
    </div>
  );
}

/** The second line of a fact. It wraps at the condensed scales rather than
 *  truncating: "2-pac PU · 2 coats" cut to "2-pac PU ·" is worse than two
 *  lines, and the column scrolls anyway. */
function Sub({ z, urgent, children }: { z: Scale; urgent?: boolean; children: string }) {
  const tight = z.lines <= 1;
  return (
    <div
      style={{
        fontSize: z.sub,
        color: urgent ? 'var(--urgent)' : 'var(--ink-muted)',
        fontWeight: urgent ? 600 : undefined,
        lineHeight: 1.25,
        overflow: 'hidden',
        ...(tight
          ? { whiteSpace: 'nowrap', textOverflow: 'ellipsis' }
          : { overflowWrap: 'break-word' }),
      }}
    >
      {children}
    </div>
  );
}

/** What a job screen draws when there is no job to draw.
 *
 *  Two states reach it and both are real: the moment on a signed-in boot before
 *  the board has answered, and a shop on its first morning with nothing taken
 *  in yet. Every empty state says what to do next (IDEA.md §10), so this one
 *  points at the board rather than reporting "no data".
 */
export function NoJobOpen() {
  const gutter = usePageGutter();
  const go = useApp((s) => s.go);
  return (
    <section style={{ flex: 1, padding: withGutter(gutter, 22, 60) }}>
      <h1 style={{ fontSize: 26, margin: '0 0 6px' }}>No job open</h1>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ink-muted)', maxWidth: 460 }}>
        Pick a job from the board to see its items, its tasks and the photos on it.
      </p>
      <button type="button" className="btn btn-primary" onClick={() => go('board')}>
        Go to the board
      </button>
    </section>
  );
}
